/**
 * Pinned-post commit confirmation.
 *
 * ## Why this does not poll `getProfile` on the PDS
 *
 * The pin flow used to confirm its write by polling `app.bsky.actor.getProfile`
 * through `new Agent(session)` — i.e. the user's PDS, which proxies to the AppView.
 * PDS responses are `cache-control: private`, so that poll always saw fresh data and
 * confirmed within ~1-3s. It then called `invalidateQueries` and let React Query
 * refetch the feed.
 *
 * But the feed is not read over that transport. `fetchAuthorFeed` reads through the
 * unauthenticated `publicAgent`, pointed at the selected AppView's public API
 * (`public.api.bsky.app` by default), which answers `cache-control: public, max-age=30`
 * from behind a CDN. The post-confirmation refetch reissued a byte-identical GET
 * inside that window and was served the stale pre-pin body — `cdn-cache: HIT`,
 * `transferSize: 0`, no network at all. The banner never moved, and because the stale
 * post carries no `isPinned`, `usePosts` re-derived `false` and the menu label flipped
 * back to "Pin to profile" on a pin that had actually succeeded.
 *
 * Neither layer answers to request headers: the CDN edge ignores request
 * `Cache-Control: no-cache`/`Pragma`, and `@atproto/xrpc`'s fetch handler passes no
 * `cache` option to `fetch()`. The edge *is* keyed on the full URL, though, and while
 * the typed client builds its query string strictly from the lexicon's declared params,
 * the custom `fetch` installed below receives the finished URL and can append to it —
 * which is how the cache-buster in `createCacheBypassingAgent` gets past the edge.
 * Both configured AppViews (`public.api.bsky.app`, `api.blacksky.community`) answer 200
 * and ignore the extra param; only the CDN, which keys on it, notices.
 *
 * ## What this does instead
 *
 * 1. Confirms against **the same transport the feed reads from** — `getAuthorFeed` on
 *    the selected AppView, with `includePins: true` — so a confirmation is proof about
 *    the bytes the feed will actually show, not about a different endpoint's view.
 * 2. Issues those polls through a short-lived `AtpAgent` whose custom `fetch` sets
 *    `cache: 'no-store'` (defeats the *browser's* HTTP cache, the layer that was
 *    returning `transferSize: 0` with no request at all) and appends a unique
 *    cache-busting query param (defeats the *CDN edge*, which keys on the full URL).
 *    Together those make every poll an origin read.
 * 3. Still only accepts a response that already shows the expected pin state, and still
 *    polls across a window longer than the CDN's 30s TTL. The cache-buster removes the
 *    edge from the picture, but AppView indexing is asynchronous regardless, so success
 *    stays self-verifying rather than assumed.
 * 4. Returns the confirmed page itself, so the caller can write those exact bytes into
 *    the query cache rather than triggering a second fetch that could hit the CDN again.
 */
import { AtpAgent } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { fetchAuthorFeed } from '@/services/postsService'
import { PINNED_POST_NETWORK_TIMEOUT_MS } from '@/services/profileService'
import { PINNED_POST_FEED_FILTER } from '@/lib/postsQueryKeys'
import type { PostsPage } from '@/types/posts'

/** Number of feed polls in a single commit-confirmation round. */
export const PINNED_POST_COMMIT_TRIES = 5
/** Delay between polls within a round. */
export const PINNED_POST_COMMIT_INTERVAL_MS = 1_000
/**
 * Extra back-off before rounds two, three and four.
 *
 * Total window: 4 rounds x 4s of intra-round waiting + 5s + 15s + 20s of back-off
 * ≈ 56s of wall clock, deliberately well past the AppView CDN's 30s `max-age`. A
 * shorter window can spend its whole life inside one cache entry and never observe
 * the pin, which is exactly the failure this module exists to prevent.
 */
export const PINNED_POST_COMMIT_RETRY_DELAYS_MS: readonly number[] = [5_000, 15_000, 20_000]

export interface PinnedPostConfirmOptions {
    /** The actor whose feed the profile is showing — the same identifier the feed query uses. */
    identifier: string
    /**
     * The AppView agent the feed reads from. Only its service URL is used: the poll runs
     * on a cache-bypassing clone so the selected AppView (Bluesky, Blacksky, …) is
     * honoured without mutating the shared agent.
     */
    publicAgent: AtpAgent
    /** Bluesky session, so the confirmed page carries the same viewer state the feed does. */
    session?: OAuthSession
    /** Page size — must match the feed query's, or the confirmed page isn't a valid substitute. */
    limit: number
}

/** Query param appended to defeat the AppView CDN's URL-keyed edge cache. */
export const CACHE_BUST_PARAM = '_cb'

let cacheBustCounter = 0

/**
 * Append a value no edge entry can already be keyed on.
 *
 * `Date.now()` alone is not enough: two polls inside the same millisecond would share a
 * URL, and the second could be served the first's freshly-cached body. The counter makes
 * every request from this tab unique.
 *
 * A non-URL input (a `Request`) is passed through untouched — `@atproto/xrpc` always
 * calls `fetch` with a string URL, so this is a defensive branch, not a live path.
 */
export function withCacheBust(input: RequestInfo | URL): RequestInfo | URL {
    if (typeof input !== 'string' && !(input instanceof URL)) return input
    try {
        const url = new URL(input.toString())
        url.searchParams.set(CACHE_BUST_PARAM, `${Date.now()}-${cacheBustCounter++}`)
        return url.toString()
    } catch {
        // Not an absolute URL — leave it alone rather than mangle the request.
        return input
    }
}

/**
 * A sibling of `publicAgent` that reads past both caches in front of the AppView: the
 * browser's HTTP cache (`cache: 'no-store'`) and the CDN edge (a unique query param,
 * which the edge keys on and the AppView ignores).
 *
 * Exported for the contract test: the AppView is taken from the caller's agent rather
 * than hardcoded, so a non-default AppView selection still reads the AppView the feed is
 * actually reading.
 */
export function createCacheBypassingAgent(publicAgent: AtpAgent): AtpAgent {
    return new AtpAgent({
        service: publicAgent.serviceUrl,
        fetch: (input, init) =>
            globalThis.fetch(withCacheBust(input), {
                ...init,
                cache: 'no-store',
                // @atproto/xrpc passes no signal of its own for these calls; bound the
                // request the same way the PDS-side pin writes are bounded.
                signal: init?.signal ?? AbortSignal.timeout(PINNED_POST_NETWORK_TIMEOUT_MS),
            }),
    })
}

/** The URI of the page's pinned-slot row, or null when the page shows no pin. */
function pinnedUriOf(page: PostsPage): string | null {
    return page.posts.find(p => p.pinnedEntry)?.uri ?? null
}

/**
 * One round of polling. Resolves with the confirmed page, or null if this round never
 * saw the expected state.
 *
 * @param expectedUri the URI expected in the pinned slot, or `null` after an unpin
 */
export async function waitForPinnedPostCommit(
    expectedUri: string | null,
    options: PinnedPostConfirmOptions,
    agent: AtpAgent
): Promise<PostsPage | null> {
    let lastError: unknown
    for (let attempt = 0; attempt < PINNED_POST_COMMIT_TRIES; attempt++) {
        try {
            const page = await fetchAuthorFeed(options.identifier, {
                limit: options.limit,
                filter: PINNED_POST_FEED_FILTER,
                session: options.session,
                publicAgent: agent,
                includePins: true,
            })
            // fetchAuthorFeed falls back to the PDS via GraphQL when the AppView errors
            // or answers empty. That page carries no pin information at all, so it can
            // neither confirm nor deny — and must never be written into the feed cache
            // in place of an AppView page.
            if (!page.usedFallback && pinnedUriOf(page) === expectedUri) return page
        } catch (err) {
            lastError = err
        }
        if (attempt < PINNED_POST_COMMIT_TRIES - 1) {
            await new Promise(resolve => setTimeout(resolve, PINNED_POST_COMMIT_INTERVAL_MS))
        }
    }
    if (lastError) console.warn('[pinnedPost] commit poll saw errors:', lastError)
    return null
}

/**
 * Poll the author feed until it reflects the pin/unpin just written to the PDS.
 *
 * @param expectedUri the URI we expect in the pinned slot, or `null` after an unpin
 * @returns the confirmed page — hand it straight to `setQueryData`, do not refetch —
 *          or `null` if every round timed out, in which case the caller must keep its
 *          optimistic state rather than refetch into the stale CDN body.
 */
export async function confirmPinnedPostCommit(
    expectedUri: string | null,
    options: PinnedPostConfirmOptions
): Promise<PostsPage | null> {
    const agent = createCacheBypassingAgent(options.publicAgent)

    const first = await waitForPinnedPostCommit(expectedUri, options, agent)
    if (first) return first

    for (const delay of PINNED_POST_COMMIT_RETRY_DELAYS_MS) {
        await new Promise(resolve => setTimeout(resolve, delay))
        const page = await waitForPinnedPostCommit(expectedUri, options, agent)
        if (page) return page
    }
    return null
}
